import { useState } from "react";
import { cn } from "@workspace/web-ui/lib/utils";

interface ImagesDocsSectionProps {
    imageUrls: string[];
    fileUrls: string[];
    onImageClick: (url: string, index: number) => void;
}

export const ImagesDocsSection = ({ imageUrls, fileUrls, onImageClick }: ImagesDocsSectionProps) => {
    const [activeTab, setActiveTab] = useState<"images" | "documents">("images");

    const renderImages = (gridCols: string = "grid-cols-2") => (
        Array.isArray(imageUrls) && imageUrls.length > 0 ? (
            <div className={cn("grid gap-3", gridCols)}>
                {imageUrls.map((imgUrl: string, idx: number) => (
                    <button
                        key={idx}
                        onClick={() => onImageClick(imgUrl, idx)}
                        className="block cursor-pointer hover:opacity-80 transition-opacity"
                    >
                        {imgUrl ? (
                            <img
                                src={imgUrl}
                                alt={`Refill Image ${idx + 1}`}
                                className="w-full h-32 md:h-24 object-cover rounded-md border bg-gray-100"
                            />
                        ) : (
                            <div className="w-full h-32 md:h-24 flex items-center justify-center rounded-md border bg-gray-100 text-gray-400">
                                <span className="text-xs">No image</span>
                            </div>
                        )}
                    </button>
                ))}
            </div>
        ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                <span className="text-3xl mb-2">🖼️</span>
                <span className="text-sm text-center px-2">No images available</span>
            </div>
        )
    );

    const renderDocuments = (gridCols: string = "grid-cols-2") => (
        Array.isArray(fileUrls) && fileUrls.length > 0 ? (
            <div className={cn("grid gap-3", gridCols)}>
                {fileUrls.map((fileUrl: string, idx: number) => {
                    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileUrl);
                    return (
                        <button
                            key={idx}
                            onClick={() => onImageClick(fileUrl, idx)}
                            className="block cursor-pointer hover:opacity-80 transition-opacity"
                        >
                            {isImage && fileUrl ? (
                                <img
                                    src={fileUrl}
                                    alt={`Lab Document ${idx + 1}`}
                                    className="w-full h-32 md:h-24 object-cover rounded-md border bg-gray-100"
                                />
                            ) : (
                                <div className="w-full h-32 md:h-24 flex items-center justify-center rounded-md border bg-gray-100 text-gray-400">
                                    <span className="text-2xl">📄</span>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                <span className="text-3xl mb-2">🗂️</span>
                <span className="text-sm text-center px-2">No lab documents available</span>
            </div>
        )
    );

    return (
        <div className="w-full mt-4">
            {/* Mobile View - Tabs */}
            <div className="md:hidden">
                {/* Tab Headers */}
                <div className="flex border-b border-gray-200 mb-4">
                    <button
                        onClick={() => setActiveTab("images")}
                        className={cn(
                            "flex-1 py-3 text-sm font-medium text-center transition-colors",
                            activeTab === "images"
                                ? "text-[#4272DD] border-b-2 border-[#4272DD]"
                                : "text-gray-500 hover:text-gray-700"
                        )}
                    >
                        Images
                    </button>
                    <button
                        onClick={() => setActiveTab("documents")}
                        className={cn(
                            "flex-1 py-3 text-sm font-medium text-center transition-colors",
                            activeTab === "documents"
                                ? "text-[#4272DD] border-b-2 border-[#4272DD]"
                                : "text-gray-500 hover:text-gray-700"
                        )}
                    >
                        Lab Documents
                    </button>
                </div>

                {/* Tab Content */}
                <div className="bg-white rounded-lg p-4 min-h-[200px]">
                    {activeTab === "images" ? renderImages("grid-cols-2") : renderDocuments("grid-cols-2")}
                </div>
            </div>

            {/* Desktop View - Grid */}
            <div className="hidden md:grid grid-cols-2 gap-6">
                {/* Images Section */}
                <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] flex flex-col">
                    <h3 className="text-lg font-semibold mb-4">Images</h3>
                    {renderImages("grid-cols-2")}
                </div>

                {/* Lab Documents Section */}
                <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] flex flex-col">
                    <h3 className="text-lg font-semibold mb-4">Lab Documents</h3>
                    {renderDocuments("grid-cols-2")}
                </div>
            </div>
        </div>
    );
};

// Version for inside batch/collapsible cards (white background)
export const ImagesDocsSectionWhite = ({ imageUrls, fileUrls, onImageClick }: ImagesDocsSectionProps) => {
    const [activeTab, setActiveTab] = useState<"images" | "documents">("images");

    const renderImages = (gridCols: string = "grid-cols-2") => (
        Array.isArray(imageUrls) && imageUrls.length > 0 ? (
            <div className={cn("grid gap-3", gridCols)}>
                {imageUrls.map((imgUrl: string, idx: number) => (
                    <button
                        key={idx}
                        onClick={() => onImageClick(imgUrl, idx)}
                        className="block cursor-pointer hover:opacity-80 transition-opacity"
                    >
                        {imgUrl ? (
                            <img
                                src={imgUrl}
                                alt={`Refill Image ${idx + 1}`}
                                className="w-full h-32 md:h-24 object-cover rounded-md border bg-gray-100"
                            />
                        ) : (
                            <div className="w-full h-32 md:h-24 flex items-center justify-center rounded-md border bg-gray-100 text-gray-400">
                                <span className="text-xs">No image</span>
                            </div>
                        )}
                    </button>
                ))}
            </div>
        ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                <span className="text-3xl mb-2">🖼️</span>
                <span className="text-sm text-center px-2">No images available</span>
            </div>
        )
    );

    const renderDocuments = (gridCols: string = "grid-cols-2") => (
        Array.isArray(fileUrls) && fileUrls.length > 0 ? (
            <div className={cn("grid gap-3", gridCols)}>
                {fileUrls.map((fileUrl: string, idx: number) => {
                    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileUrl);
                    return (
                        <button
                            key={idx}
                            onClick={() => onImageClick(fileUrl, idx)}
                            className="block cursor-pointer hover:opacity-80 transition-opacity"
                        >
                            {isImage && fileUrl ? (
                                <img
                                    src={fileUrl}
                                    alt={`Lab Document ${idx + 1}`}
                                    className="w-full h-32 md:h-24 object-cover rounded-md border bg-gray-100"
                                />
                            ) : (
                                <div className="w-full h-32 md:h-24 flex items-center justify-center rounded-md border bg-gray-100 text-gray-400">
                                    <span className="text-2xl">📄</span>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                <span className="text-3xl mb-2">🗂️</span>
                <span className="text-sm text-center px-2">No lab documents available</span>
            </div>
        )
    );

    return (
        <div className="w-full mt-4">
            {/* Mobile View - Tabs */}
            <div className="md:hidden">
                {/* Tab Headers */}
                <div className="flex border-b border-gray-200 mb-4">
                    <button
                        onClick={() => setActiveTab("images")}
                        className={cn(
                            "flex-1 py-3 text-sm font-medium text-center transition-colors",
                            activeTab === "images"
                                ? "text-[#4272DD] border-b-2 border-[#4272DD]"
                                : "text-gray-500 hover:text-gray-700"
                        )}
                    >
                        Images
                    </button>
                    <button
                        onClick={() => setActiveTab("documents")}
                        className={cn(
                            "flex-1 py-3 text-sm font-medium text-center transition-colors",
                            activeTab === "documents"
                                ? "text-[#4272DD] border-b-2 border-[#4272DD]"
                                : "text-gray-500 hover:text-gray-700"
                        )}
                    >
                        Lab Documents
                    </button>
                </div>

                {/* Tab Content */}
                <div className="bg-white rounded-lg p-4 min-h-[200px]">
                    {activeTab === "images" ? renderImages("grid-cols-2") : renderDocuments("grid-cols-2")}
                </div>
            </div>

            {/* Desktop View - Grid */}
            <div className="hidden md:grid grid-cols-2 gap-6">
                {/* Images Section */}
                <div className="bg-white rounded-lg p-4 min-h-[200px] flex flex-col">
                    <h3 className="text-lg font-semibold mb-4">Images</h3>
                    {renderImages("grid-cols-2")}
                </div>

                {/* Lab Documents Section */}
                <div className="bg-white rounded-lg p-4 min-h-[200px] flex flex-col">
                    <h3 className="text-lg font-semibold mb-4">Lab Documents</h3>
                    {renderDocuments("grid-cols-2")}
                </div>
            </div>
        </div>
    );
};

